/**
 * plan.js — один список правок вместо двух несовместимых режимов.
 *
 * ЗАЧЕМ ОТДЕЛЬНЫЙ ФАЙЛ. rank.js умеет выбрасывать предложения, trim.js — снимать
 * лишнее внутри предложения. Пока это два инструмента, пользователю приходится
 * решать, каким пользоваться, — а он не знает и не должен: он знает только своё
 * число. Здесь решение принимается за него и по измеримому признаку — насколько
 * далеко текст от цели.
 *
 * ПРАВИЛО РЕШЕНИЯ, И ПОЧЕМУ ИМЕННО ТАКОЕ. Дешевле всего то, что не теряет
 * утверждений. Поэтому сначала пробуем уложиться одними словами (ярусы 0–3);
 * если словами дотянуть нельзя — только тогда выбираем предложения, и уже на
 * выживших добираем словами РОВНО столько, сколько не хватает. Обратный порядок
 * («сначала предложения») даёт тот самый топор: чтобы снять 17 знаков, уходит
 * целая фраза, а промах — в другую сторону и на порядок больше.
 *
 * МИНИМАЛЬНОЕ ВМЕШАТЕЛЬСТВО — ЧАСТЬ КОНТРАКТА. Правки берутся по ярусам снизу
 * вверх и остановка происходит на первой, после которой цель достигнута.
 * Инструмент, который «сжимает как получится», нельзя использовать второй раз
 * на своём же тексте: он каждый раз забирает больше, чем просили.
 *
 * ВЫБОР ПРЕДЛОЖЕНИЙ СЧИТАЕТ УЖЕ СОКРАЩЁННЫЙ РАЗМЕР. Иначе бюджет тратится на
 * воду внутри фраз, и в результат попадает меньше утверждений, чем помещается.
 *
 * Exposed as window.TSPlan.
 */
(function (root) {
    'use strict';
    if (root.TSPlan) return;

    var TIERS = [0, 1, 2, 3];   // wordy → filler → hedge → aside; предложения (4) отдельно

    function rank() { return root.TSRank; }
    function trim() { return root.TSTrim; }

    /* ──────────────────────────────────────────────────────────────────
       СЧЁТ ПО ПРАВИЛАМ ПЛОЩАДКИ.

       «Влезает в 280» и «влезает в пост» — разные утверждения, если площадка
       считает иначе, чем String.length. Две самые частые ловушки:

       SMS. 160 знаков — только у алфавита GSM-7. Одна кириллическая буква,
       длинное тире «—» или «ёлочки» переводят всё сообщение в UCS-2, и предел
       становится 70, а длинное режется на сегменты по 67. Пресет «SMS · 160»,
       стоявший здесь до 2026-10-02, для русского текста был неверен вдвое.

       X. Ссылка любой длины весит 23 знака; эмодзи, иероглифы и хангыль — по 2
       (twitter-text v3: вес 1 только у кодовых точек 0–4351 и типографских
       пробелов/кавычек 8192–8205, 8208–8223, 8242–8247). Корейский пост в X
       вмещает 140 слогов, а не 280.
       ────────────────────────────────────────────────────────────────── */
    var GSM_BASIC = '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡'
        + 'ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà';
    var GSM_EXT = '^{}\\[~]|€\f';
    var GSM_B = {}, GSM_E = {};
    GSM_BASIC.split('').forEach(function (c) { GSM_B[c] = 1; });
    GSM_EXT.split('').forEach(function (c) { GSM_E[c] = 1; });

    /** { gsm, units, limit, perSegment, segments } — как оператор посчитает SMS. */
    function smsInfo(text) {
        var s = String(text || ''), gsm = true, sept = 0;
        for (var i = 0; i < s.length; i++) {
            var c = s[i];
            if (GSM_B[c]) sept += 1;
            else if (GSM_E[c]) sept += 2;
            else { gsm = false; break; }
        }
        var units = gsm ? sept : s.length;           // UCS-2 = кодовые единицы UTF-16
        var limit = gsm ? 160 : 70, per = gsm ? 153 : 67;
        return {
            gsm: gsm, units: units, limit: limit, perSegment: per,
            segments: units <= limit ? (units ? 1 : 0) : Math.ceil(units / per),
        };
    }

    var X_URL = /(?:https?:\/\/|www\.)\S+|\b[\w-]+(?:\.[\w-]+)*\.(?:com|org|net|ru|рф|io|co|me|info|biz|app|dev|ai|uk|de|fr|es|pt|br|id|kr|ua|tv|ly|gl|be)\b(?:\/\S*)?/gi;
    var PICTO = null;
    try { PICTO = new RegExp('\\p{Extended_Pictographic}', 'u'); } catch (e) { PICTO = null; }
    var GRAPH = null;
    try { if (typeof Intl !== 'undefined' && Intl.Segmenter) GRAPH = new Intl.Segmenter('en', { granularity: 'grapheme' }); } catch (e) { GRAPH = null; }

    function cpWeight(cp) {
        return (cp <= 4351 || (cp >= 8192 && cp <= 8205) || (cp >= 8208 && cp <= 8223)
            || (cp >= 8242 && cp <= 8247)) ? 1 : 2;
    }

    /** Длина поста так, как её считает X. */
    function xLength(text) {
        var s = String(text || '');
        try { s = s.normalize('NFC'); } catch (e) {}
        var n = 0;
        s = s.replace(X_URL, function () { n += 23; return ''; });
        var parts = [];
        if (GRAPH) { for (var g of GRAPH.segment(s)) parts.push(g.segment); }
        else parts = Array.from ? Array.from(s) : s.split('');
        for (var i = 0; i < parts.length; i++) {
            var p = parts[i];
            if (PICTO && PICTO.test(p)) { n += 2; continue; }
            for (var j = 0; j < p.length; j++) {
                var cp = p.codePointAt(j);
                if (cp > 0xFFFF) j++;
                n += cpWeight(cp);
            }
        }
        return n;
    }

    /**
     * Размер текста в единицах цели.
     * unit: 'chars' | 'words' | 'sentences'; rule (только для знаков): 'x' | 'sms'.
     */
    function sizeOf(text, unit, lang, rule) {
        if (unit === 'words') return trim().countWords(text, lang);
        if (unit === 'sentences') return String(text || '').trim() ? rank().split(text).length : 0;
        if (rule === 'x') return xLength(text);
        if (rule === 'sms') return smsInfo(text).units;
        return String(text || '').length;
    }

    /** Сколько знаков или слов освобождает операция, если её применить. */
    function saving(src, op, unit, lang, rule) {
        var was = src.slice(op.s, op.e);
        if (unit === 'words') return Math.max(0, trim().countWords(was, lang) - trim().countWords(op.rep || '', lang));
        if (rule === 'x' || rule === 'sms') return Math.max(0, sizeOf(was, 'chars', lang, rule) - sizeOf(op.rep || '', 'chars', lang, rule));
        return Math.max(0, was.length - (op.rep || '').length);
    }

    /**
     * Целевое число в знаках/словах/предложениях. `percent` считается ВСЕГДА от
     * знаков — «сократить на 30%» о длине текста, а не о числе слов, и смешивать
     * эти два счёта в одном поле значит показывать пользователю чужую цифру.
     */
    function resolveTarget(text, opts) {
        var unit = opts.unit || 'chars';
        var n = Math.max(1, parseInt(opts.target, 10) || 0);
        if (unit === 'percent') {
            return { unit: 'chars', target: Math.max(1, Math.round(String(text).length * Math.min(100, n) / 100)) };
        }
        return { unit: unit, target: n };
    }

    /* ──────────────────────────────────────────────────────────────────
       РАЗБОР ТЕКСТА ЖИВЁТ ДОЛЬШЕ ЦЕЛИ.

       Ползунок цели пересчитывает план на каждом кадре протяжки, а текст при
       этом не меняется. Разбивка на предложения, поиск правок и центральность
       (она квадратична по числу предложений) зависят только от текста и
       языка — их считаем один раз. Замер 2026-10-02: 23 тыс. знаков — 156 мс
       на план без кэша; с кэшем протяжка стоит выбор и сборку.
       Правки копируются на каждый план: finish() метит их (`on`, `_i`), и
       общий объект протёк бы из одного плана в другой.
       ────────────────────────────────────────────────────────────────── */
    var CACHE = { key: null };
    function analysis(src, lang) {
        var key = lang + '|' + src.length + '|' + src;
        if (CACHE.key !== key) CACHE = { key: key, src: src, lang: lang, sentences: rank().split(src) };
        return CACHE;
    }
    function opsOf(A) {
        if (!A.ops) A.ops = trim().ops(A.src, { lang: A.lang, sentences: A.sentences });
        return A.ops;
    }
    /* Центральность считается по СОДЕРЖАНИЮ фразы — без того, что пословная
       правка и так признала водой. Иначе «таким образом», «в конечном итоге»,
       «стоит отметить» голосовали за предложение наравне с его предметом, и
       выбор зависел от того, сколько в фразе служебных оборотов. */
    function scoresOf(A) {
        if (A.scores) return A.scores;
        var byS = {};
        opsOf(A).forEach(function (o) {
            var i = indexOfSentence(A.sentences, o.s);
            if (i >= 0) (byS[i] = byS[i] || []).push(o);
        });
        var content = A.sentences.map(function (s, i) {
            var local = (byS[i] || []).map(function (o) {
                return { s: o.s - s.start, e: Math.min(o.e, s.end) - s.start, rep: o.rep, cap: o.cap };
            });
            return { text: local.length ? trim().apply(s.text, local) : s.text, para: s.para, start: s.start, end: s.end };
        });
        A.scores = rank().score(content);
        /* Вывод весит больше, чем его словарь. «Таким образом, экономия
           времени… весомее экономии на бумаге» — главная мысль примера, но по
           словам она ближе к концу списка: про «экономию» и «согласование»,
           а не про «документы». Сигнальные слова вывода в начале фразы —
           классический признак (Edmundson, 1969); сам оборот при этом пословная
           правка уберёт, а мысль останется. */
        A.sentences.forEach(function (s, i) { if (CONCLUDES.test(s.text)) A.scores[i] *= 1.6; });
        return A.scores;
    }
    var CONCLUDES = new RegExp('^["«“„(]?(?:'
        + 'таким образом|итак|в итоге|в конечном итоге|в конечном сч[её]те|в результате|следовательно|'
        + 'в заключение|подводя итог|'
        + 'таким чином|отже|у підсумку|в підсумку|у кінцевому підсумку|в кінцевому підсумку|'
        + 'у результаті|в результаті|'
        + 'thus|therefore|in conclusion|as a result|overall|in summary|to sum up|hence|consequently|'
        + 'daher|deshalb|zusammenfassend|insgesamt|folglich|'
        + 'por lo tanto|en conclusión|en resumen|en definitiva|'
        + 'donc|ainsi|en conclusion|en résumé|par conséquent|'
        + 'portanto|em conclusão|em resumo|em suma|'
        + 'jadi|oleh karena itu|kesimpulannya|dengan demikian|singkatnya'
        + ')(?![\\p{L}\\p{N}-])', 'iu');
    /* Повтор считается по НАКОПЛЕННОМУ множеству основ, а не склейкой уже
       выбранного текста: склейка перечитывалась целиком на каждом кандидате,
       и глава в 70 тыс. знаков тратила на это почти полсекунды за план. */
    function termsOf(A, i) {
        if (!A.terms) A.terms = [];
        return A.terms[i] || (A.terms[i] = rank()._terms(A.sentences[i].text));
    }
    function repeats(seen, ts) {
        if (!ts.length) return 0;
        var hit = 0;
        for (var j = 0; j < ts.length; j++) if (seen[ts[j]]) hit++;
        return hit / ts.length;
    }
    function remember(seen, ts) { for (var j = 0; j < ts.length; j++) seen[ts[j]] = 1; }

    /* ══════════════════════════════════════════════════════════════════
       ЧТО ЗА ТЕКСТ И ИЗ ЧЕГО ОН СОСТОИТ (2026-10-03).

       Обращение Фарида: сказка на 6 600 знаков, цель 1 093 — «выдаёт
       какую-то глупость». Разбор показал три причины, и все три — про
       устройство текста, а не про числа:

       1. РАССКАЗ ≠ СТАТЬЯ. У статьи главное — то, о чём говорят все её
          фразы (центральность). У рассказа главное — завязка, повороты и
          развязка, а «центральной» по словам оказывается болтовня героев:
          реплики повторяют имена и слова сюжета чаще повествования. Поэтому
          у текста с репликами вес реплик ниже, а первой и последней фразы —
          выше.
       2. РЕПЛИКА — ОДНА ЕДИНИЦА. «— Порфирий! — воскликнул толстый. — Ты ли
          это?» нельзя резать посередине: оставшееся «Голубчик мой!» без тире
          читается обрывком. Абзац-реплика уходит или остаётся целиком.
       3. СВЯЗКИ. Фраза на «Поэтому», «Это», «Тогда», «However» без
          предыдущей висит в воздухе — её либо оставляют вместе с той, на
          которую она ссылается, либо не оставляют.
       Заголовки выбираются отдельно: заголовок раздела нужен, только если
       от раздела что-то осталось.
       ══════════════════════════════════════════════════════════════════ */
    var DIALOGUE = /^(?:[-—–][ \t ]*\S|[«"„“])/;
    var CONNECTOR = new RegExp('^[-—–«"„“ \\t\\u00A0]*(?:(?:именно|вот|саме|exactly|just)\\s+)?(?:'
        + 'поэтому|потому|тогда|это|этот|эта|эти|этого|этой|этим|там|тут|так|также|тоже|однако|но|зато|'
        + 'при этом|кроме того|в результате|следовательно|после этого|затем|потом|впрочем|причём|причем|'
        + 'тому|тоді|це|цей|ця|ці|там|також|проте|але|потім|після цього|'
        + 'however|but|so|thus|therefore|this|these|that|those|there|then|also|moreover|after that|'
        + 'daher|deshalb|dann|dies|diese|dieser|dort|aber|jedoch|auch|'
        + 'por eso|entonces|esto|este|esta|estos|estas|eso|allí|pero|también|sin embargo|'
        + 'donc|alors|cela|ce|cette|ces|là|mais|aussi|cependant|'
        + 'por isso|então|isso|isto|esse|essa|lá|mas|também|porém|'
        + 'karena itu|lalu|kemudian|ini|itu|tetapi|namun'
        + ')(?![\\p{L}\\p{N}-])', 'iu');

    var SPEECH = new RegExp('(?:^|[^\\p{L}])(?:'
        + 'я|мне|меня|мной|мы|нас|нам|ты|тебе|тебя|тобой|вы|вас|вам|вами|'
        + 'мой|моя|моё|мое|мои|моего|моей|твой|твоя|твоё|твое|твои|ваш|ваша|ваше|ваши|наш|наша|наше|наши|'
        + 'мені|мене|ти|тобі|тебе|ви|вам|'
        + 'i|me|my|we|us|you|your|'
        + 'ich|mir|mich|du|dir|dich|wir|ihr|'
        + 'yo|tú|te|nosotros|vosotros|usted|'
        + 'je|moi|tu|toi|nous|vous|'
        + 'eu|você|nós|'
        + 'aku|saya|kamu|kami|kita|anda'
        + ')(?![\\p{L}])', 'iu');

    var CALL_TO_ACTION = new RegExp('^[-—–«"„“ \\t\\u00A0]*(?:'
        + 'закажите|купите|оформите|звоните|позвоните|пишите|успейте|приходите|подпишитесь|переходите|'
        + 'замовте|купуйте|телефонуйте|'
        + 'order|buy|shop|call|get yours|subscribe|sign up|'
        + 'bestellen sie|kaufen sie|jetzt|compre|pide|commandez|achetez|peça|beli|pesan'
        + ')(?![\\p{L}\\p{N}-])', 'iu');
    /* Указательное слово в начале фразы отсылает к предыдущей: «Если такой
       вариант вас устраивает» без самого варианта бессмысленно. */
    var POINTS_BACK = new RegExp('^(?:\\S+\\s+){0,2}(?:'
        + 'такой|такая|такое|такие|такого|такую|таким|данный|данная|данное|данные|указанный|указанная|'
        + 'этот|эта|эти|этого|этой|этих|этим|'
        + 'такий|така|таке|такі|цей|ця|ці|'
        + 'such|this|these|those|'
        + 'solch|diese|dieser|dieses|tal|tales|este|esta|estos|estas|ce|cette|ces|tel|telle|esse|essa|tais|tersebut'
        + ')(?![\\p{L}\\p{N}-])', 'iu');

    function isHeading(s) {
        var t = s.text;
        if (/^=+[^=]+=+$/.test(t)) return true;                       // «== История ==» из Википедии
        if (t.length > 90 || DIALOGUE.test(t)) return false;
        if (/^\p{Lu}[\p{Lu}\s.!-]{1,30}$/u.test(t) && /\p{Lu}{3}/u.test(t)) return true;   // «КОНЕЦ.»
        return !/[.!?…:;,]$/.test(t) && t.split(/\s+/).length <= 12;
    }

    function profileOf(A) {
        if (A.profile) return A.profile;
        var ss = A.sentences, n = ss.length;
        var heading = ss.map(isHeading);
        /* Реплика — весь абзац, начатый тире, а не только его первая фраза:
           «— Милый мой! — начал тонкий. Я уже женат, как видишь… Это вот моя
           жена…» — продолжение речи без тире, и раньше оно считалось
           повествованием и попадало в «три главных предложения» Чехова. */
        var paraFirst = {};
        ss.forEach(function (s, i) { if (paraFirst[s.para] === undefined) paraFirst[s.para] = i; });
        var multiPara = Object.keys(paraFirst).length >= 3;
        var dialogue = ss.map(function (s) {
            return DIALOGUE.test(s.text) || (multiPara && DIALOGUE.test(ss[paraFirst[s.para]].text));
        });
        /* В тексте без абзацев (переводы строк потерялись) продолжение реплики
           узнаётся по первому-второму лицу сразу после фразы с тире: «- Не
           волнуйся, я успею! Я точно успею до рассвета!» */
        for (var c = 1; c < n; c++) {
            if (!dialogue[c] && !multiPara && ss[c].para === ss[c - 1].para && dialogue[c - 1]
                && SPEECH.test(ss[c].text)) dialogue[c] = true;
        }
        var dCount = dialogue.filter(Boolean).length;
        var narrative = dCount >= 3 && dCount / Math.max(1, n) >= 0.15;
        /* Единицы: обычно предложение; абзац-реплика (начинается с тире, не
           длиннее 10 фраз и 900 знаков) — одна единица целиком: реплика,
           разрезанная посередине, оставляет обрывок без тире. */
        var units = [], unitOf = new Array(n);
        var i = 0;
        while (i < n) {
            if (dialogue[i] && !heading[i]) {
                var j = i, len = 0;
                while (j < n && ss[j].para === ss[i].para) { len += ss[j].text.length; j++; }
                var sameParaIsReplica = (j - i) <= 10 && len <= 900
                    && (i === 0 || ss[i - 1].para !== ss[i].para);
                if (sameParaIsReplica && j - i > 1) {
                    var grp = [];
                    for (var k = i; k < j; k++) { grp.push(k); unitOf[k] = units.length; }
                    units.push(grp);
                    i = j;
                    continue;
                }
            }
            unitOf[i] = units.length;
            units.push([i]);
            i++;
        }
        A.profile = { narrative: narrative, dialogue: dialogue, heading: heading, units: units, unitOf: unitOf };
        return A.profile;
    }

    /** Вес предложения для выбора: центральность + жанр + повторы. */
    function weightsOf(A) {
        if (A.weights) return A.weights;
        var raw = scoresOf(A), P = profileOf(A), ss = A.sentences, n = ss.length;
        var w = raw.slice(), seen = {};
        raw.map(function (_, i) { return i; }).sort(function (a, b) { return raw[b] - raw[a]; })
            .forEach(function (i) {
                if (repeats(seen, termsOf(A, i)) > 0.72) w[i] *= 0.3;
                remember(seen, termsOf(A, i));
            });
        var top = Math.max.apply(null, w.concat([0]));
        var first = -1, last = -1;
        for (var a = 0; a < n; a++) if (!P.heading[a]) { first = a; break; }
        for (var b = n - 1; b >= 0; b--) if (!P.heading[b]) { last = b; break; }
        if (P.narrative) {
            /* Реплика без тире — в тексте, где переводы строк пропали («Ну, всё
               ступай, и запомни…» после реплики кота), — узнаётся по первому и
               второму лицу: повествование в третьем лице их не носит. */
            for (var i = 0; i < n; i++) {
                if (P.dialogue[i]) w[i] *= 0.45;
                else if (SPEECH.test(ss[i].text)) w[i] *= 0.6;
            }
            // Завязка всегда весит как самое важное в тексте, развязка — почти.
            if (first >= 0) w[first] = Math.max(w[first], top) * 1.5;
            if (first + 1 < n && !P.dialogue[first + 1] && !P.heading[first + 1]) w[first + 1] *= 1.35;
            if (last > first) w[last] = Math.max(w[last], top * 0.9) * 1.2;
        } else {
            if (first >= 0) w[first] *= 1.35;                           // тема статьи — в первой фразе
            /* Число — это факт («до 30 часов», «72 миллиарда», «IPX5»), а
               призыв «Закажите сегодня!» — нет: в описании товара на 500 знаков
               раньше оставался призыв и терялась автономность (замер
               2026-10-03). */
            for (var e = 0; e < n; e++) {
                if (/\d/.test(ss[e].text)) w[e] *= 1.25;
                if (CALL_TO_ACTION.test(ss[e].text)) w[e] *= 0.55;
            }
        }
        var sorted = raw.slice().sort(function (x, y) { return x - y; });
        A.weights = { w: w, raw: raw, median: sorted[Math.floor(sorted.length / 2)] || 0 };
        return A.weights;
    }

    /* ══════════════════════════════════════════════════════════════════
       ПОКРЫТИЕ (2026-10-03). Снимать «наименее центральное» мало: на
       сказке про мальчика и щенка центральность тянет фразы с частым
       «Петя», и при сжатии вчетверо из текста уходил сам щенок — а финал
       («а Дружок всегда был рядом») оставался без того, о ком он. Предмет,
       который встречается в тексте не раз, — нить сюжета или темы, и
       ПОСЛЕДНЯЯ оставшаяся фраза о нём стоит дороже своей центральности.
       Ключ предмета — грубее основы ранжира: «щенок/щенка», «мама/маму/
       мамой», «вода/водой» должны совпасть, иначе нить не видна.
       ══════════════════════════════════════════════════════════════════ */
    function coverKey(t) {
        var k = String(t);
        if (/[а-яёіїєґ]/.test(k)) {
            if (k.length >= 4) {
                var c = k.replace(/(ами|ями|ого|его|ому|ему|ой|ей|ом|ем|ам|ям|ах|ях|ую|юю|ая|яя|ое|ее|ые|ие|ий|ый|ов|ев|а|я|о|е|у|ю|ы|и|й|ь|і|ї)$/, '');
                if (c.length >= 3) k = c;
            }
            // Беглая гласная: «щенок → щенка», «дружок → дружка», «конец → конца».
            if (k.length >= 5) k = k.replace(/([бвгджзклмнпрстфхцчшщ])(?:о|е|ё)(к|ц)$/, '$1$2');
        } else if (/^[a-z]+$/.test(k) && k.length >= 5) {
            k = k.replace(/(ies|es|s|ed|ing)$/, '').replace(/y$/, '');
        }
        return k;
    }

    /**
     * Снимает единицы по одной, пока `more()`: каждый раз — ту, что сейчас
     * дешевле всех. Цена — вес единицы, умноженный на то, сколько нитей
     * текста оборвётся вместе с ней (предметы, у которых она осталась
     * последней). Куча с ленивыми записями: цена только растёт, и
     * пересчитывается лишь у последнего держателя предмета.
     */
    var COVER = 1.5;
    function removeByCoverage(A, units, uw, us, kept, cover, more, drop) {
        var uterms = units.map(function (u) {
            var set = {};
            u.forEach(function (i) { termsOf(A, i).forEach(function (t) { set[coverKey(t)] = 1; }); });
            return Object.keys(set);
        });
        var df = {}, holders = {};
        uterms.forEach(function (ts, k) {
            ts.forEach(function (t) { df[t] = (df[t] || 0) + 1; (holders[t] = holders[t] || []).push(k); });
        });
        var cnt = {};
        Object.keys(df).forEach(function (t) { cnt[t] = df[t]; });
        /* Прибавка — в долях МЕДИАНЫ весов, а не своего веса: у фразы о
           предмете центральность бывает вчетверо ниже, чем у фраз с именем
           героя, и умножение её не спасало. */
        var sorted = uw.slice().sort(function (a, b) { return a - b; });
        var med = sorted[Math.floor(sorted.length / 2)] || 0;
        var cost = function (k) {
            var bonus = 0;
            uterms[k].forEach(function (t) { if (df[t] >= 2 && cnt[t] === 1) bonus += Math.min(1, (df[t] - 1) / 2); });
            return uw[k] + cover * med * bonus;
        };
        var heap = [], ver = units.map(function () { return 0; });
        var less = function (a, b) { return a.c < b.c || (a.c === b.c && us[a.k] > us[b.k]); };
        var push = function (e) {
            heap.push(e);
            for (var i = heap.length - 1; i > 0;) {
                var p = (i - 1) >> 1;
                if (!less(heap[i], heap[p])) break;
                var tmp = heap[i]; heap[i] = heap[p]; heap[p] = tmp; i = p;
            }
        };
        var pop = function () {
            var top = heap[0], last = heap.pop();
            if (heap.length) {
                heap[0] = last;
                for (var i = 0; ;) {
                    var l = 2 * i + 1, r = l + 1, m = i;
                    if (l < heap.length && less(heap[l], heap[m])) m = l;
                    if (r < heap.length && less(heap[r], heap[m])) m = r;
                    if (m === i) break;
                    var tmp = heap[i]; heap[i] = heap[m]; heap[m] = tmp; i = m;
                }
            }
            return top;
        };
        units.forEach(function (_, k) { push({ c: cost(k), k: k, v: 0 }); });
        while (heap.length && more()) {
            var e = pop();
            if (!kept[e.k] || e.v !== ver[e.k]) continue;
            drop(e.k);
            uterms[e.k].forEach(function (t) {
                cnt[t]--;
                if (cnt[t] !== 1 || df[t] < 2) return;
                var hs = holders[t];
                for (var j = 0; j < hs.length; j++) {
                    if (kept[hs[j]]) { ver[hs[j]]++; push({ c: cost(hs[j]), k: hs[j], v: ver[hs[j]] }); break; }
                }
            });
        }
    }

    /**
     * Какие предложения оставить, чтобы уложиться в `target` (в единицах
     * `lean` — уже сокращённых словами размерах). Снимаем снизу по цене
     * единиц (вес и покрытие), возвращаем по весу, что влезает в остаток,
     * держим связки и заголовки. → { i: 1 } оставленных.
     */
    function pickUnits(A, lean, target) {
        var P = profileOf(A), W = weightsOf(A), w = W.w;
        var units = P.units.filter(function (u) { return !P.heading[u[0]]; });
        var uw = units.map(function (u) { return Math.max.apply(null, u.map(function (i) { return w[i]; })); });
        var us = units.map(function (u) { return u.reduce(function (t, i) { return t + lean[i]; }, 0); });
        var offTopic = function (k) {
            return Math.max.apply(null, units[k].map(function (i) { return W.raw[i]; })) < W.median * 0.25 && !P.narrative;
        };
        /* Фраза, которой нужна предыдущая: связка («Поэтому…») и продолжение
           речи внутри длинной реплики — без начала реплики (с тире) оно
           читается как слова автора. */
        var startsWithLink = function (k) {
            var i0 = units[k][0], t = A.sentences[i0].text;
            return CONNECTOR.test(t) || (P.dialogue[i0] && !DIALOGUE.test(t))
                || (!P.narrative && POINTS_BACK.test(t));
        };
        var prevSamePara = function (k) {
            return k > 0 && A.sentences[units[k - 1][0]].para === A.sentences[units[k][0]].para ? k - 1 : -1;
        };

        var kept = units.map(function () { return true; });
        var used = us.reduce(function (t, x) { return t + x; }, 0), keptN = units.length;
        /* Покрытие — только у рассказа: у статьи последняя фраза о редком
           предмете — периферия («археи», «сера» в статье о фотосинтезе
           вытесняли определение процесса, замер 2026-10-03), а у рассказа —
           нить сюжета. */
        removeByCoverage(A, units, uw, us, kept, P.narrative ? COVER : 0,
            function () { return used > target && keptN > 1; },
            function (k) { kept[k] = false; used -= us[k]; keptN--; });
        /* Осталась одна единица, и она длиннее цели («одна SMS» из статьи), —
           лучше самая важная из тех, что ВЛЕЗАЮТ. Не влезает ни одна —
           остаётся главная, и недобор называется числом. */
        if (used > target) {
            var fit = -1;
            units.forEach(function (_, k) {
                if (us[k] <= target && !offTopic(k) && (fit < 0 || uw[k] > uw[fit])) fit = k;
            });
            if (fit >= 0) {
                kept = units.map(function (_, k) { return k === fit; });
                used = us[fit];
            }
        }
        /* Слова автора на двоеточии («…а кот ей снова говорит:») обещают
           реплику СЛЕДОМ: без неё фраза указывает на чужую. */
        var announces = function (k) {
            var u = units[k];
            return /:$/.test(A.sentences[u[u.length - 1]].text) && k + 1 < units.length;
        };
        var keptCount = function () { var c = 0; kept.forEach(function (x) { if (x) c++; }); return c; };
        /* Связка без своей опоры висит в воздухе — такую не оставляем.
           Проходы с конца и до устойчивости: опора сама может требовать
           опору («Вот сюрприз!» → «— Вот не ожидал!» → начало реплики), и
           цепочка раскручивается только так. */
        /* Опоры добавляются ВСЕЙ цепочкой или не добавляются вовсе: по одной
           они раскачивали выбор — опора требовала свою опору, бюджет кончался,
           её снимали, и висящая фраза оставалась («Это, Нафаня, друг моего
           детства!» без «А это сын мой…», замер 2026-10-03). */
        var chainOf = function (k) {
            var chain = [], cur = k;
            while (startsWithLink(cur)) {
                var p = prevSamePara(cur);
                if (p < 0 || kept[p]) break;
                chain.push(p);
                cur = p;
            }
            return chain;
        };
        var settleLinks = function () {
            for (var pass = 0; pass < 12; pass++) {
                var changed = false;
                for (var k = units.length - 1; k >= 0; k--) {
                    if (!kept[k]) continue;
                    var chain = chainOf(k);
                    if (chain.length) {
                        var cost = chain.reduce(function (t, x) { return t + us[x]; }, 0);
                        if (used + cost <= target) {
                            chain.forEach(function (x) { kept[x] = true; });
                            used += cost;
                        } else if (keptCount() > 1) {
                            kept[k] = false; used -= us[k];
                        }
                        changed = true;
                        continue;
                    }
                    if (announces(k) && !kept[k + 1]) {
                        if (used + us[k + 1] <= target) { kept[k + 1] = true; used += us[k + 1]; }
                        else if (keptCount() > 1) { kept[k] = false; used -= us[k]; }
                        changed = true;
                    }
                }
                if (!changed) break;
            }
        };
        settleLinks();
        // Остаток бюджета — важнейшим из убранного, если влезают.
        units.map(function (_, k) { return k; }).filter(function (k) { return !kept[k]; })
            .sort(function (a, b) { return uw[b] - uw[a]; })
            .forEach(function (k) {
                if (offTopic(k) || used + us[k] > target) return;
                if (startsWithLink(k)) { var p = prevSamePara(k); if (p >= 0 && !kept[p]) return; }
                if (announces(k) && !kept[k + 1]) return;
                kept[k] = true; used += us[k];
            });
        settleLinks();

        var set = {};
        units.forEach(function (u, k) { if (kept[k]) u.forEach(function (i) { set[i] = 1; }); });
        // Заголовок нужен, только если от его раздела что-то осталось; заглавие
        // текста — если влезает.
        var n = A.sentences.length;
        for (var h = 0; h < n; h++) {
            if (!P.heading[h]) continue;
            var anyKept = false;
            for (var x = h + 1; x < n && !P.heading[x]; x++) if (set[x]) { anyKept = true; break; }
            var isTitle = h === 0 && A.sentences[h].text.length <= 120;
            if ((anyKept || (isTitle && n > 2)) && used + lean[h] <= target) { set[h] = 1; used += lean[h]; }
        }
        return set;
    }

    /**
     * make(text, opts) → план правок.
     *
     * opts: { target, unit: 'chars'|'words'|'percent', lang, off: {cls: true} }
     *
     * Возвращает:
     *   ops       — ВСЕ найденные правки, включая невыбранные (`on:false`):
     *               пользователь должен видеть не только сделанное, но и то,
     *               что инструмент мог сделать и не стал.
     *   regime    — 'fits' | 'words' | 'sentences'
     *   stats     — по классам: сколько правок и сколько они дали
     *   shortfall — на сколько цель НЕ достигнута (0, если достигнута)
     */
    function make(text, opts) {
        opts = opts || {};
        /* Переводы строк Windows сводятся к «\n» ЗДЕСЬ, до разбора: split()
           считает позиции по сведённой строке, и правки, применённые к
           исходной с «\r\n», резали слова посередине («стемну и бабушка»,
           замер 2026-10-03 на тексте из файла). Страница берёт src из плана. */
        var src = String(text || '').replace(/\r\n?/g, '\n');
        var lang = opts.lang || 'ru';
        var off = opts.off || {};
        var rt = resolveTarget(src, opts);
        var unit = rt.unit, target = rt.target;
        var rule = unit === 'chars' ? (opts.rule || null) : null;

        var A = analysis(src, lang);
        var sentences = A.sentences;
        var srcSize = sizeOf(src, unit, lang, rule);

        var base = {
            src: src, sentences: sentences, unit: unit, target: target,
            srcSize: srcSize, lang: lang, rule: rule,
        };

        if (!sentences.length) {
            return finish(base, [], 'fits');
        }
        /* Влезающий текст не трогается ВОВСЕ — даже чисткой швов: она
           возвращает пробел в склеенное «дому.Долго», и текст, влезавший по
           исходнику (1088 из 1093), выходил на 1105 — а план, честно
           досчитав, выбрасывал ради лишних знаков целое предложение (замер
           2026-10-03, сказка с сайта). Без правок TSTrim.apply отдаёт текст
           как есть. */
        if (srcSize <= target) {
            return finish(base, [], 'fits');
        }

        /* ЦЕЛЬ В ПРЕДЛОЖЕНИЯХ («сократи до 3 предложений» — 1 004 показа в
           месяц, «до 1 предложения» — 842, Вордстат 2026-10-02). Здесь
           пословная правка ничего не даёт — число предложений от неё не
           меняется, — поэтому выбор идёт сразу по центральности, а слова
           внутри оставленных фраз не трогаются: минимальное вмешательство
           остаётся правилом и здесь. */
        if (unit === 'sentences') {
            if (off.sent) return finish(base, [], 'words');
            /* Тот же вес, что и при выборе по знакам: у рассказа — завязка и
               развязка, а реплики дешевле повествования; заголовки в счёт не
               идут (три «главных предложения» — это не «Введение»). */
            var sc = weightsOf(A).w;
            var heads = profileOf(A).heading;
            var ord = sentences.map(function (_, i) { return i; })
                .filter(function (i) { return !heads[i]; })
                .sort(function (a, b) { return sc[b] - sc[a]; });
            var keepN = {}, kept = 0, seenN = {};
            for (var q = 0; q < ord.length && kept < target; q++) {
                var ix = ord[q];
                if (kept && repeats(seenN, termsOf(A, ix)) > 0.72) continue;
                keepN[ix] = 1; kept++; remember(seenN, termsOf(A, ix));
            }
            // Повторы отсеяны, а мест осталось — добираем их по порядку важности.
            for (var r = 0; r < ord.length && kept < target; r++) {
                if (!keepN[ord[r]]) { keepN[ord[r]] = 1; kept++; }
            }
            var drops = [];
            sentences.forEach(function (s, i) {
                if (keepN[i]) return;
                drops.push({ s: s.start, e: s.end, rep: '', cls: 'sent', tier: 4, cap: false, save: 1 });
            });
            return finish(base, drops, 'sentences');
        }

        var wordOps = opsOf(A)
            .filter(function (o) { return !off[o.cls]; })
            .map(function (o) { return Object.assign({}, o); });
        wordOps.forEach(function (o) { o.save = saving(src, o, unit, lang, rule); });

        // ── попытка 1: уложиться, не теряя ни одного предложения ──────────
        // Сумма экономий — оценка: чистка швов меняет итог на несколько знаков
        // в обе стороны. Поэтому попадание проверяется настоящим размером, и
        // недобор добирается следующими правками той же лестницы.
        var need = srcSize - target;
        var picked = greedy(wordOps, need);
        for (var g = 0; g < 4 && picked.total >= need; g++) {
            var real = sizeOf(trim().apply(src, picked.ops), unit, lang, rule);
            if (real <= target) return finish(base, picked.ops, 'words');
            need += real - target;
            picked = greedy(wordOps, need);
        }

        // ── попытка 2: предложения тоже придётся выбирать ─────────────────
        // Бюджет считается по УЖЕ сокращённому размеру фразы: иначе вода внутри
        // предложений съедает место, которое досталось бы ещё одному
        // утверждению.
        var byS = {};
        wordOps.forEach(function (o) {
            var i = indexOfSentence(sentences, o.s);
            if (i < 0) return;
            (byS[i] = byS[i] || []).push(o);
        });

        /* Если выбрасывать предложения запретили (класс выключен), дальше идти
           некуда: отдаём всё, что дали слова, и называем недобор числом. */
        if (off.sent) {
            return finish(base, picked.ops, 'words');
        }

        var scores = scoresOf(A);
        var full = sentences.map(function (s) { return sizeOf(s.text, unit, lang, rule) + (unit === 'words' ? 0 : 1); });
        var lean = sentences.map(function (s, i) {
            var local = (byS[i] || []).map(function (o) {
                return { s: o.s - s.start, e: o.e - s.start, rep: o.rep, cap: o.cap };
            });
            var t = local.length ? trim().apply(s.text, local) : s.text;
            return sizeOf(t, unit, lang, rule) + (unit === 'words' ? 0 : 1);
        });

        /* СНИМАЕМ СНИЗУ, А НЕ НАБИРАЕМ СВЕРХУ.

           До 2026-10-02 предложения НАБИРАЛИСЬ по важности, пока влезают. На
           собственном примере это давало абсурд: при цели 75 % длинный вывод
           («Таким образом, экономия времени… весомее, чем экономия на бумаге»)
           не влезал в остаток и пропускался, а короткое постороннее «Погода
           вчера была ясной…» — влезало и оставалось. Рюкзак заполнялся мусором.

           Теперь наоборот: убираем наименее связанные с темой, пока текст не
           уложится, и только потом возвращаем убранное, что влезает в остаток, —
           по важности и НИКОГДА не постороннее (центральность меньше четверти
           медианы: такое предложение не о том, о чём текст). Повтор уже
           сказанного весит втрое меньше — пересказ не тратит место дважды. */
        var keptSet = pickUnits(A, lean, target);
        var keep = sentences.map(function (_, i) { return i; }).filter(function (i) { return keptSet[i]; });

        // Выброшенные предложения — такие же операции: один список, одна отмена.
        var sentOps = [];
        sentences.forEach(function (s, i) {
            if (keptSet[i]) return;
            sentOps.push({
                s: s.start, e: s.end, rep: '', cls: 'sent', tier: 4, cap: false,
                save: unit === 'words' ? trim().countWords(s.text, lang) : sizeOf(s.text, 'chars', lang, rule),
            });
        });

        // На выживших добираем словами РОВНО столько, сколько не хватает.
        var keptRaw = 0;
        keep.forEach(function (i) { keptRaw += full[i]; });
        var need2 = keptRaw - target;
        var inKept = wordOps.filter(function (o) { return keptSet[indexOfSentence(sentences, o.s)]; });
        var picked2 = greedy(inKept, need2);

        /* СВЕРКА ПО НАСТОЯЩЕМУ ТЕКСТУ. Размеры выше — оценка: разделитель
           между фразами считается одним знаком, а между абзацами их два
           («\n\n»), пробел после склеенного знака добавляет tidy(). На сказке
           в абзацах это давало 1 097 при цели 1 093. Пока собранный текст
           длиннее цели, добираем остальные пословные правки, а когда их нет —
           снимаем самую лёгкую из оставленных фраз (кроме последней). */
        var all = sentOps.concat(picked2.ops);
        var W = weightsOf(A).w;
        for (var guard = 0; guard < 40; guard++) {
            var size = sizeOf(trim().apply(src, all), unit, lang, rule);
            if (size <= target) break;
            var chosenIds = {};
            all.forEach(function (o) { chosenIds[o.s + ':' + o.e] = 1; });
            var spare = inKept.filter(function (o) {
                return !chosenIds[o.s + ':' + o.e] && o.save > 0 && keptSet[indexOfSentence(sentences, o.s)];
            }).sort(function (a, b) { return a.tier - b.tier || b.save - a.save; })[0];
            if (spare) { all.push(spare); continue; }
            var keptNow = keep.filter(function (i) { return keptSet[i]; });
            if (keptNow.length <= 1) break;
            var lightest = keptNow.sort(function (a, b) { return W[a] - W[b]; })[0];
            delete keptSet[lightest];
            var ls = sentences[lightest];
            all = all.filter(function (o) { return !(o.s >= ls.start && o.e <= ls.end); });
            all.push({ s: ls.start, e: ls.end, rep: '', cls: 'sent', tier: 4, cap: false,
                save: unit === 'words' ? trim().countWords(ls.text, lang) : sizeOf(ls.text, 'chars', lang, rule) });
        }

        return finish(base, all, 'sentences');
    }

    /** Берём по ярусам снизу вверх, внутри яруса — крупное первым, и СТОП. */
    function greedy(ops, need) {
        var chosen = [], total = 0;
        if (need <= 0) return { ops: chosen, total: 0 };
        for (var t = 0; t < TIERS.length; t++) {
            var tier = ops.filter(function (o) { return o.tier === TIERS[t] && o.save > 0; })
                .sort(function (a, b) { return b.save - a.save; });
            for (var i = 0; i < tier.length; i++) {
                if (total >= need) return { ops: chosen, total: total };
                chosen.push(tier[i]);
                total += tier[i].save;
            }
        }
        return { ops: chosen, total: total };
    }

    /* Двоичный поиск: предложения идут по порядку и не пересекаются. */
    function indexOfSentence(sentences, pos) {
        var lo = 0, hi = sentences.length - 1;
        while (lo <= hi) {
            var mid = (lo + hi) >> 1, sm = sentences[mid];
            if (pos < sm.start) hi = mid - 1;
            else if (pos >= sm.end) lo = mid + 1;
            else return mid;
        }
        return -1;
    }

    var CLASSES = ['wordy', 'filler', 'hedge', 'aside', 'sent'];

    function finish(base, applied, regime) {
        var src = base.src;

        /* Пересечения возможны на стыке: границы словесных правок захватывают
           соседний пробел и могут на один знак заезжать в чужое предложение.
           Оставленные как есть, они дают двойной счёт в сводке и рваный показ,
           поэтому крупная правка (выброшенное предложение) поглощает мелкую. */
        var sorted = applied.slice().sort(function (a, b) {
            return a.s !== b.s ? a.s - b.s : (b.e - b.s) - (a.e - a.s);
        });
        var all = [], reach = -1;
        sorted.forEach(function (o) {
            if (o.s < reach) return;
            all.push(o);
            reach = o.e;
        });
        all.forEach(function (o, i) { o.on = true; o._i = i; });

        var out = trim().apply(src, all);
        var size = sizeOf(out, base.unit, base.lang, base.rule);

        var stats = {};
        CLASSES.forEach(function (c) { stats[c] = { n: 0, save: 0 }; });
        all.forEach(function (o) {
            var st = stats[o.cls] || (stats[o.cls] = { n: 0, save: 0 });
            st.n++;
            st.save += o.save || 0;
        });

        return {
            src: src,
            text: out,
            ops: all,
            sentences: base.sentences,
            unit: base.unit,
            rule: base.rule || null,
            target: base.target,
            srcSize: base.srcSize,
            size: size,
            regime: regime,
            stats: stats,
            shortfall: Math.max(0, size - base.target),
            cutPct: base.srcSize ? Math.max(0, Math.round((1 - size / base.srcSize) * 100)) : 0,
        };
    }

    /**
     * render(src, ops) → плоский список кусков для показа.
     *   { t: 'keep' | 'cut' | 'rep', text, rep, i }
     * `i` — индекс операции в списке, по нему страница включает и выключает.
     * Выключенная операция отдаётся как 'keep': текст на месте, а нажатие
     * по-прежнему её касается.
     */
    function render(src, ops) {
        var s = String(src || '');
        var out = [], last = 0;
        ops.slice().sort(function (a, b) { return a.s - b.s; }).forEach(function (op, i) {
            if (op.s < last) return;
            if (op.s > last) out.push({ t: 'keep', text: s.slice(last, op.s) });
            out.push({
                t: op.on === false ? 'off' : (op.rep ? 'rep' : 'cut'),
                text: s.slice(op.s, op.e),
                rep: op.rep || '',
                cls: op.cls,
                i: op._i === undefined ? i : op._i,
            });
            last = op.e;
        });
        if (last < s.length) out.push({ t: 'keep', text: s.slice(last) });
        return out;
    }

    /** Текст по текущему состоянию переключателей. */
    function assemble(src, ops) {
        return trim().apply(src, ops.filter(function (o) { return o.on !== false; }));
    }

    root.TSPlan = {
        make: make,
        render: render,
        assemble: assemble,
        size: sizeOf,
        resolveTarget: resolveTarget,
        smsInfo: smsInfo,
        xLength: xLength,
        CLASSES: CLASSES,
        /** Для тестов и разбора: веса и единицы, по которым идёт выбор. */
        _analyze: function (text, lang) {
            var A = analysis(String(text || '').replace(/\r\n?/g, '\n'), lang || 'ru');
            var W = weightsOf(A), P = profileOf(A);
            return {
                sentences: A.sentences.map(function (s) { return s.text; }),
                w: W.w, raw: W.raw, units: P.units, narrative: P.narrative,
                terms: A.sentences.map(function (_, i) { return termsOf(A, i).map(coverKey); }),
            };
        },
    };
})(typeof self !== 'undefined' ? self : this);
